import React, { FC } from "react";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { RasterIcon } from "components/Icon/Icon";
import { IconType } from "enums/Icon";
import { List } from "immutable";
import { useTranslation } from "react-i18next";
import { requestNames } from "reduxFolder/reducers/serviceRequests";
import {
  getAddonDescription,
  getAddonPrice,
  getAddonsIconsImage,
} from "../../../deliveryHelper";
import { DefaultItemIcon } from "../../../../../styles/parcelsStyles";
import {
  Description,
  HeadText,
  HeadTextCompact,
  Item,
  ItemCompact,
  ItemHead,
  ItemHeadContent,
  ItemHeadPrice,
  ItemIcon,
  ItemLogo,
  ItemSection,
  ListItem,
  ListItemCompact,
  ServiceRequestIcon,
} from "./SideAddonItem.styles";

interface SideAddonItemProps {
  itemAddons: any[];
  allItemsAddons?: any[];
  addons: any;
  cartItem: any;
  consolidationAddons?: any;
  showAsCompact?: boolean;
}

const SideAddonItem: FC<SideAddonItemProps> = ({
  itemAddons,
  allItemsAddons,
  addons,
  consolidationAddons,
  cartItem,
  showAsCompact,
}) => {
  const { t } = useTranslation("common");
  const currencySymbol = useCurrencySymbol();
  const uniqueAddons = allItemsAddons
    ? itemAddons.filter((x) => !allItemsAddons.includes(x))
    : itemAddons;

  const hasAddons = !!(itemAddons && itemAddons.length && uniqueAddons.length);
  const allAddons =
    addons.size && consolidationAddons.size
      ? List(addons).merge(consolidationAddons)
      : List();
  const logoUrl = cartItem.get("emblem_thumb_url");
  const heading = cartItem.get("shipment_tracking_number");
  const description = cartItem.get("customer_comment");
  const serviceRequests = cartItem.get("service_requests", List());

  if (!hasAddons) {
    return null;
  }

  if (showAsCompact) {
    return (
      <ItemCompact>
        {logoUrl ? <ItemLogo image={logoUrl} size={20} /> : <DefaultItemIcon />}
        <HeadTextCompact>{heading}</HeadTextCompact>
        {serviceRequests.map((request: any) => {
          const iconUrl = request.getIn(["service_request_type", "icon", "url"]);
          const typeName = request.getIn(["service_request_type", "type"])?.toLowerCase();
          const mappedType = typeName ? requestNames[typeName as keyof typeof requestNames] : null;

          return (
            <ListItemCompact key={request.get("id")}>
              {iconUrl ? (
                <RasterIcon icon={iconUrl} size={[16, 16]} />
              ) : mappedType ? (
                <ServiceRequestIcon type={mappedType as IconType} />
              ) : null}
            </ListItemCompact>
          );
        })}
        {uniqueAddons.map((id) => (
          <ListItemCompact key={id}>
            <ItemIcon>{getAddonsIconsImage(id, allAddons)}</ItemIcon>
          </ListItemCompact>
        ))}
      </ItemCompact>
    );
  }

  let price = 0;
  uniqueAddons.forEach(
    (id) => (price += parseFloat(getAddonPrice(id, addons))),
  );
  serviceRequests.forEach(
    (request: any) =>
      (price += parseFloat(request.getIn(["service_request_type", "cost"]))),
  );

  return (
    <Item>
      <ItemHead withDescription={!!description}>
        {logoUrl ? <ItemLogo image={logoUrl} size={20} /> : <DefaultItemIcon />}
        <ItemHeadContent>
          <HeadText>
            {heading}:{description && <Description>{description}</Description>}
          </HeadText>
          <ItemHeadPrice>
            {price !== 0
              ? `${currencySymbol}${price.toFixed(2)}`
              : t("common.free", { currencySymbol })}
          </ItemHeadPrice>
        </ItemHeadContent>
      </ItemHead>
      <ItemSection>
        {serviceRequests.map((request: any) => {
          const iconUrl = request.getIn(["service_request_type", "icon", "url"]);
          const typeName = request.getIn(["service_request_type", "type"])?.toLowerCase();
          const mappedType = typeName ? requestNames[typeName as keyof typeof requestNames] : null;

          return (
            <ListItem key={request.get("id")}>
              {iconUrl ? (
                <RasterIcon icon={iconUrl} size={[16, 16]} />
              ) : mappedType ? (
                <ServiceRequestIcon type={mappedType as IconType} />
              ) : null}
              <span>{t(`serviceRequests.${mappedType || typeName}.title`)}</span>
            </ListItem>
          );
        })}

        {uniqueAddons.map((id) => (
          <ListItem key={id}>
            <ItemIcon>{getAddonsIconsImage(id, allAddons)}</ItemIcon>
            {getAddonDescription(id, allAddons)}
          </ListItem>
        ))}
      </ItemSection>
    </Item>
  );
};

export default React.memo(SideAddonItem);
